import type { RumBeforeSend } from '@datadog/browser-rum';

import type { ContextualData, JourneyEventContextualData } from '../contextualData';
import { emptyContextualData } from '../contextualData';
import type { DatadogContext, DatadogOptions } from '../declarations/Clients';
import type { EventTrackingAction } from '../declarations/EventTracking';
import type { JourneyEventWrapper } from '../declarations/JourneyEvent';
import { globalObservability } from '../global';

// Browsers redact cross-origin script failures to these messages without a usable source or stack.
// Restricting the filter to source-collected errors preserves explicit application errors.
const OPAQUE_SCRIPT_ERROR_MESSAGES = ['Script error.', 'Uncaught "Script error."'];

const shouldSendDefaultRumEvent: RumBeforeSend = (event) =>
  !(
    event.type === 'error' &&
    event.error.source === 'source' &&
    OPAQUE_SCRIPT_ERROR_MESSAGES.includes(event.error.message)
  );

export const buildDefaultDatadogOptions = (opts: {
  appName: string;
  env: string;
  version: string;
}): Partial<DatadogOptions> => {
  const { appName, env, version } = opts;
  return {
    site: 'datadoghq.com',
    version,
    env,
    service: appName,
    // The SDK defaults this to true, which appends a `baggage` header (session, user and
    // account ids) to every request matching allowedTracingUrls. Our services' CORS
    // allowlists do not accept `baggage`, so it fails preflight and breaks those requests.
    // Apps may opt in once every traced origin accepts the header.
    propagateTraceBaggage: false,
    beforeSend: shouldSendDefaultRumEvent,
  };
};

export const buildDatadogOptions = (
  defaults: { appName: string; env: string; version: string },
  overrides: DatadogOptions,
): DatadogOptions => {
  const defaultOptions = buildDefaultDatadogOptions(defaults);
  const defaultBeforeSend = defaultOptions.beforeSend;
  const appBeforeSend = overrides.beforeSend;

  return {
    ...defaultOptions,
    ...overrides,
    beforeSend: (event, context) => {
      if (defaultBeforeSend?.(event, context) === false) {
        return false;
      }
      return appBeforeSend?.(event, context) ?? true;
    },
  };
};

export function getContextualDataFromDatadog(): ContextualData {
  const sessionId = globalObservability.getDatadogRumClient().getInternalContext?.()?.session_id;
  if (sessionId) {
    return {
      data: {
        'datadog-rum-session-id': sessionId,
        'datadog-rum-session-replay': `https://app.datadoghq.com/rum/replay/sessions/${sessionId}`,
        'datadog-rum-session-search': `https://app.datadoghq.com/rum/explorer?query=%40session.id%3A${sessionId}`,
      },
    };
  } else {
    return emptyContextualData;
  }
}

export function trackPerformanceInDatadog(eventName: string, time?: number) {
  const client = globalObservability.getDatadogRumClient();
  if (time === undefined) {
    client.addTiming?.(eventName);
    return;
  }
  client.addTiming?.(eventName, time);
}

export function addRUMGlobalContext(context: DatadogContext) {
  globalObservability.getDatadogRumClient()?.setGlobalContext?.({
    ...globalObservability.getDatadogRumClient().getGlobalContext?.(),
    ...context,
  });
}

export function trackEventInDatadog<AppActions>(
  // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
  action: EventTrackingAction<AppActions>,
  label?: string,
  contextualData: ContextualData = emptyContextualData,
) {
  globalObservability.getDatadogRumClient().addAction?.(action, {
    ...contextualData.data,
    adClickContext: contextualData.adClickContext,
    campaignContext: contextualData.campaignContext,
    customerContext: contextualData.customerContext,
    exclusiveOffersContext: contextualData.exclusiveOffersContext,
    leadContext: contextualData.leadContext,
    sellDirectContext: contextualData.sellDirectContext,
    label,
  });
}

export function trackJourneyEventInDatadog(
  event: Partial<JourneyEventWrapper>,
  contextualData: JourneyEventContextualData,
) {
  const category = Object.keys(event)[0] ?? 'unknownUserEvent';
  globalObservability.getDatadogRumClient().addAction?.(category, {
    ...event,
    campaignContext: contextualData.campaignContext,
    ...(contextualData.propertyPageContext && {
      propertyPageContext: contextualData.propertyPageContext,
    }),
  });
}

export function trackErrorInDatadog(
  label: string,
  error?: Error,
  contextualData: ContextualData = emptyContextualData,
) {
  if (shouldExcludeErrorByMessage(error)) {
    return;
  }

  globalObservability.getDatadogRumClient().addError?.(error || new Error(label), {
    ...contextualData.data,
    label,
  });
}

// Array will perform better than Set when N is tiny by avoiding a hashing operation. Consider converting to a
// Set<string> if N ever becomes large, and after benchmarking performance.
const ERROR_MESSAGES_TO_EXCLUDE: string[] = [
  // Error that MAY indicate a performance issue due to browser implementation, but has been shown to be safe to ignore.
  // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  'ResizeObserver loop limit exceeded',
];

export const shouldExcludeErrorByMessage = (error?: Error): boolean => {
  return ERROR_MESSAGES_TO_EXCLUDE.includes(error?.message || '');
};

/**
 * Set the user for the RUM client.
 * id is required. Other properties are optional.
 * Refer to https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=640andabove#user-session
 * @param user
 */
export function setRUMUser(user: { id: string; [k: string]: unknown }) {
  globalObservability.getDatadogRumClient().setUser?.(user);
}

export function setRUMUserProperty(user: { [k: string]: unknown }) {
  Object.entries(user).forEach(([key, value]) => {
    globalObservability.getDatadogRumClient().setUserProperty?.(key, value);
  });
}
